import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import {
  MapPin,
  MessageCircle,
  ShieldCheck,
  LineChart,
  CheckCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function DetalleListado() {
  const { id } = useParams();
  const [listado, setListado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mostrarNavFija, setMostrarNavFija] = useState(false);
  const { isPremium } = useAuth();
  const { user } = useAuth(); // Asegúrate de que tu hook lo devuelve
  const [userRole, setUserRole] = useState(null);


  // Obtener datos del listado
  useEffect(() => {
    const obtenerListado = async () => {
      try {
        const docRef = doc(db, "listings", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setListado(docSnap.data());
        }
      } catch (error) {
        console.error("Error al obtener el listado:", error);
      } finally {
        setCargando(false);
      }
    };

    obtenerListado();
  }, [id]);

  useEffect(() => {
    const obtenerRolUsuario = async () => {
      if (!user?.email) return;
  
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.email === user.email) {
            setUserRole(data.role);
          }
        });
      } catch (err) {
        console.error("Error obteniendo el rol del usuario:", err);
      }
    };
  
    obtenerRolUsuario();
  }, [user?.email]);
  

  // Detección de scroll para navbar fija
  useEffect(() => {
    const handleScroll = () => {
      setMostrarNavFija(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (cargando || !listado)
    return <div className="p-10 text-center">Cargando empresa...</div>;

  return (
    <>
      {/* Contenido principal */}
      <div className="w-full px-6 py-10 grid grid-cols-1 xl:grid-cols-[65%_1fr] gap-10 bg-gray-50">
        {/* BLOQUE IZQUIERDO */}
        <div id="resumen" className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-6">
          {/* Cabecera */}
          <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
              <span className="w-1.5 h-1.5 bg-yellow-700 rounded-full"></span>
              Agencia
            </span>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Tarragona (España)
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 leading-snug">
            Agencia de marketing B2B altamente rentable: 118 % de crecimiento anual, máquina de ventas automatizada, operaciones sistematizadas
          </h1>

          <p className="text-sm text-gray-700 leading-relaxed max-w-4xl">
            Somos una agencia digital de alto crecimiento especializada en marketing para empresas tecnológicas B2B, ofreciendo soluciones integrales con un enfoque innovador y orientado al ROI. Hemos alcanzado de forma constante crecimientos superiores al 100 % interanual…
          </p>

          {/* Precio solicitado + múltiplos */}
          <div className="pt-2">
            <h2 className="text-base font-semibold text-gray-800 mb-2">Precio solicitado</h2>
            <div className="flex items-center gap-6">
              <p className="text-xl font-bold text-gray-900">3.100.000€</p>
              <div className="text-sm text-gray-600">
                Múltiplos: <strong>3.9× beneficio</strong> · <strong>2.6× ingresos</strong>
              </div>
            </div>
          </div>

          {/* Texto de justificación */}
          <p className="text-sm text-gray-600 max-w-4xl">
            El precio se basa en los KPIs siguientes y en ser una agencia de rápido crecimiento con un 80–85 % de ingresos mensuales recurrentes.
          </p>

          {/* Puntos de justificación */}
          <ul className="space-y-2 text-sm text-gray-800 mt-2">
            {[
              "$800k+ SDE en los últimos 12 meses",
              "Margen EBITDA del 50–60 %",
              "Ingresos diversificados (ningún cliente supera el 15 %)",
              "100–150 reuniones comerciales cualificadas mensuales (<$60 CPL)",
              "CAC: ~1.5 k$ | LTV: 15–20 k$",
              "118 % de crecimiento anual medio en los últimos 5 años",
              "Operaciones limpias con SOPs sólidos y bajo coste fijo",
              "Uso proactivo de IA para mejorar márgenes",
              "Empresa preparada para adquisición inmediata",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-yellow-600 mt-[2px]" />
                {item}
              </li>
            ))}
          </ul>

          {/* Rendimiento reciente */}
          <div id="metricas" className="pt-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-3">Rendimiento reciente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "INGRESOS ÚLTIMO AÑO", value: "1.200.000€" },
                { label: "BENEFICIO ÚLTIMO AÑO", value: "810.000€" },
                { label: "INGRESOS ÚLTIMO MES", value: "200.000€" },
                { label: "BENEFICIO ÚLTIMO MES", value: "157.000€" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 border rounded-lg px-3 py-3 text-xs">
                  <div className="text-gray-500 font-medium uppercase tracking-wide mb-1">
                    {item.label}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Resumen anual */}
          <div className="pt-10">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Resumen anual</h2>
            <div className="w-full h-64 bg-white rounded-md p-4 border">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: "Ingresos", value: 1235269 }, { name: "Gastos", value: 590431 }, { name: "Beneficio", value: 809507 }] }>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(val) => `${(val / 1000).toFixed(0)}€`} />
                  <Tooltip
                    formatter={(value) => `${value.toLocaleString()}€`}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      color: '#111827',
                    }}
                    wrapperStyle={{
                      visibility: 'visible',
                    }}
                    cursor={{ fill: 'transparent' }}
                  />

                  <Bar dataKey="value" fill="#f0b632" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 overflow-hidden border border-gray-200 rounded-xl text-sm">
              <table className="min-w-full text-left">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 border-r border-gray-200"></th>
                    <th className="px-4 py-3">TTM</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  <tr>
                    <td className="px-4 py-2 text-yellow-600 font-medium underline">Ingresos</td>
                    <td className="px-4 py-2 font-medium">1.235.269€</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-yellow-600 font-medium underline">Gastos</td>
                    <td className="px-4 py-2 font-medium">590.431€</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-yellow-600 font-medium underline">Beneficio</td>
                    <td className="px-4 py-2 font-medium">809.507€</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Métricas de clientes */}
          <div className="pt-10 border-t border-dotted border-gray-300">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Clientes</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-gray-700">
              <div>
                <div className="text-xs text-gray-500 uppercase mb-1">Número de clientes</div>
                <div className="font-semibold text-yellow-600">10–50</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase mb-1">Ingresos recurrentes anuales</div>
                <div className="font-semibold text-yellow-600">1.500.000€</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase mb-1">Tasa de crecimiento anual</div>
                <div className="font-semibold text-yellow-600">118 %</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase mb-1">Tasa de cancelación</div>
                <div className="font-semibold text-yellow-600">10 % + a la baja</div>
              </div>
            </div>
          </div>

          <div id="info">
            
          </div>
          {/* Información de la empresa */}
          <div className="pt-12 border-t border-dotted border-gray-200 text-sm text-gray-800 space-y-10">
            <div>
              <h2 className="text-base font-semibold mb-4">Información de la empresa</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Fecha de fundación</p>
                  <p className="text-gray-800">Ene 2020</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">número de empleados</p>
                  <p className="text-gray-800">21–100</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Modelo de negocio</p>
              <div className="flex flex-wrap gap-2">
                {["Entre empresas (B2B)", "Publicidad", "Suscripción", "Basado en agencia", "Servicios"].map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {item}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm text-gray-600">90 % de los ingresos son retenidos (MRR)</p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Tecnologías</p>
              <div className="flex flex-wrap gap-2">
                {["OpenAI", "Webflow", "dataslayer"].map((tech, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Competidores</p>
              <div className="flex flex-wrap gap-2">
                {["Directive Consulting", "Single Grain", "WebFX", "Neil Patel Digital", "Hunch"].map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Oportunidades de crecimiento</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {["Incrementar precios", "Expandirse a nuevos mercados"].map((g, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {g}
                  </span>
                ))}
              </div>
              <p className="text-sm text-gray-600">La prioridad n.º 1 es aumentar la retención.</p>
              <p className="text-sm text-gray-600">Se han contratado 2 especialistas (mayo 2025) con bonus y KPIs centrados en la retención.</p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Activos clave</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {["Copyright", "Nombres comerciales", "Redes sociales", "Dominio", "Inventario", "Sitio web", "Marcas registradas", "Materiales de marketing", "Propiedad intelectual", "Otros", "Clientes", "Marca"].map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Detalles de adquisición */}
          <div className="pt-12 border-t border-dotted border-gray-200 text-sm text-gray-800 space-y-6">
            <h2 className="text-base font-semibold text-gray-800">Detalles de la adquisición</h2>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Motivo de venta</p>
              <div className="flex flex-wrap gap-2">
                {["Iniciar un nuevo proyecto", "Cambio de estilo de vida"].map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                    <CheckCircle className="w-4 h-4 text-yellow-600" />
                    {r}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Financiación</p>
              <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">
                <CheckCircle className="w-4 h-4 text-yellow-600" />
                Bootstrapped
              </span>
            </div>
          </div>
        </div>

        {/* BLOQUE DERECHO */}
        <div className="space-y-6" id="detalles">
        {userRole === 'premium' || userRole === 'admin' ? (
  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm space-y-4">
    <img
  src="/PREMIUM.png"
  alt="Premium"
  className="h-16 mx-auto mb-4"
/>

    {/* Iniciar chat */}
    <button className="w-full bg-yellow-600 text-white font-semibold text-sm py-2 rounded-md shadow-sm hover:bg-yellow-700 transition">
      Iniciar chat con el vendedor
    </button>

    {/* Descargar informe */}
    <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md hover:bg-yellow-50 transition">
      Descargar informe de la oferta
    </button>

    {/* Ver histórico financiero */}
    <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md hover:bg-yellow-50 transition">
      Ver histórico financiero
    </button>

    {/* Solicitar reunión 1:1 */}
    <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md hover:bg-yellow-50 transition">
      Solicitar reunión 1:1 con el fundador
    </button>

    {/* Ver contratos, clientes o activos */}
    <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md hover:bg-yellow-50 transition">
      Ver activos clave y contratos
    </button>

    {/* Marcar como favorita */}
    <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md hover:bg-yellow-50 transition">
      Añadir a favoritos
    </button>
  </div>
) : (
  
  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm space-y-4 relative overflow-hidden">
    <img
  src="/PREMIUM.png"
  alt="Premium"
  className="h-16 mx-auto mb-4"
/>
    <h3 className="text-base font-semibold text-yellow-900 leading-tight">
      Consigue acceso completo con Premium
    </h3>
    <p className="text-sm text-yellow-800">
      Chatea con el fundador y accede a datos exclusivos no públicos.
    </p>
    <a
      href="/premium"
      className="block w-full text-center bg-yellow-600 text-white text-sm font-medium py-2 rounded-md hover:bg-blue-700 transition"
    >
      Unirse →
    </a>

    {/* BLOQUE PREMIUM BORROSO */}
    <div className="mt-4 relative">
      <div className="blur-sm opacity-40 pointer-events-none select-none space-y-3">
        <button className="w-full bg-yellow-600 text-white font-semibold text-sm py-2 rounded-md shadow-sm">
          Iniciar chat con el vendedor
        </button>
        <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md">
          Descargar informe de la oferta
        </button>
        <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md">
          Ver histórico financiero
        </button>
        <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md">
          Solicitar reunión 1:1 con el fundador
        </button>
        <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md">
          Ver activos clave y contratos
        </button>
        <button className="w-full bg-white text-yellow-700 border border-yellow-300 font-medium text-sm py-2 rounded-md">
          Añadir a favoritos
        </button>
      </div>

      {/* Mensaje centrado */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center bg-yellow-100/90 px-4 py-2 rounded-md shadow text-yellow-900 font-semibold text-sm border border-yellow-300">
          Solo disponible con <strong>Premium</strong>
        </div>
      </div>
    </div>
  </div>
)
}



          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-white border rounded-lg px-4 py-3 shadow-sm text-sm text-gray-800">
              <MessageCircle className="w-5 h-5 text-yellow-600 mt-[2px]" />
              <span><strong>16</strong> compradores en conversaciones activas</span>
            </div>
            <div className="flex items-start gap-2 bg-white border rounded-lg px-4 py-3 shadow-sm text-sm text-gray-800">
            <ShieldCheck className="w-5 h-5 text-yellow-600 mt-[2px]" />
            <span className="flex items-center gap-1">
              Validado por Noltrade
              <div className="relative group cursor-pointer">
                <span className="w-4 h-4 text-[11px] text-gray-500 border border-gray-300 rounded-full flex items-center justify-center leading-none">i</span>
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-white border border-gray-200 text-xs text-gray-700 rounded-md px-3 py-2 shadow-xl whitespace-nowrap z-10">
                  La oferta ha sido revisada mediante nuestro servicio de validación con inteligencia artificial.
                </div>
              </div>
            </span>
            </div>
            <div className="flex items-start gap-2 bg-white border rounded-lg px-4 py-3 shadow-sm text-sm text-gray-800">
              <LineChart className="w-5 h-5 text-yellow-600 mt-[2px]" />
              <span>Finanzas recategorizadas para mayor claridad</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
